import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { AlertTriangle, RotateCcw, X, Eye, Loader2, Trash2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import AnimatedModal from '../../components/ui/AnimatedModal';
import CandidateInsightModal from '../../components/results/CandidateInsightModal';

const RANGE_OPTIONS = [
  { key: '7d', label: 'Last 7 days', days: 7 },
  { key: '30d', label: 'Last 30 days', days: 30 },
  { key: '90d', label: 'Last 90 days', days: 90 },
  { key: 'all', label: 'All time', days: null },
];

const isoDay = (date) => date.toISOString().split('T')[0];

/** Build {from,to} query params for the active range. */
function rangeToParams(range) {
  if (!range || range.preset === 'all') return {};
  const opt = RANGE_OPTIONS.find((o) => o.key === range.preset);
  if (opt && opt.days != null) {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - opt.days);
    return { from: isoDay(from), to: isoDay(to) };
  }
  return {};
}

const fmtUSD = (v) =>
  Number(v || 0).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

/** Open the candidate resume in a new tab (PDF previews inline; doc/docx downloads). */
const openResume = async (candidateId) => {
  try {
    const { data } = await axios.get(`/api/screenings/candidates/${candidateId}/resume-view-url`);
    if (data?.url) {
      window.open(data.url, '_blank', 'noopener,noreferrer');
    } else {
      toast.error('Resume not available');
    }
  } catch (error) {
    toast.error(error?.response?.data?.message || 'Failed to open resume');
  }
};

/**
 * Admin "Extraction Fallbacks" drill-down.
 * Recovered = LibreOffice failed but OpenAI rescued the score (monitoring).
 * Failed    = both failed → unscored, original file still viewable (action needed → Retry).
 *
 * Green dot = unread (admin-controlled, same model as the recruiter candidates table); the
 * Status dropdown toggles read/unread. The Score is a button that opens the same
 * CandidateInsightModal the recruiter sees, on the score tab.
 */
export default function ExtractionFallbacksModal({ isOpen, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('failed'); // action-needed first
  const [range, setRange] = useState({ preset: '7d' });
  const [retryingId, setRetryingId] = useState(null);
  const [dismissingId, setDismissingId] = useState(null);
  const [settingReadId, setSettingReadId] = useState(null);
  const [scoreLoadingId, setScoreLoadingId] = useState(null);
  const [selectedCandidate, setSelectedCandidate] = useState(null);

  const fetchData = useCallback(() => {
    setLoading(true);
    setError(null);
    axios
      .get('/api/internal/extraction-fallbacks', { params: rangeToParams(range) })
      .then((res) => setData(res.data))
      .catch((err) => setError(err?.response?.data?.message || 'Failed to load extraction fallbacks'))
      .finally(() => setLoading(false));
  }, [range]);

  useEffect(() => {
    if (isOpen) fetchData();
  }, [isOpen, fetchData]);

  const handleRetry = async (candidateId) => {
    setRetryingId(candidateId);
    try {
      await axios.post(`/api/internal/extraction-fallbacks/${candidateId}/retry`);
      toast.success('Re-queued for processing — refresh in a moment to see the result');
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Retry failed');
    } finally {
      setRetryingId(null);
    }
  };

  // Dismiss = "mark as handled": hides the row from the panel without touching the candidate.
  const handleDismiss = async (candidateId) => {
    setDismissingId(candidateId);
    try {
      await axios.post(`/api/internal/extraction-fallbacks/${candidateId}/dismiss`);
      toast.success('Removed from panel');
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to remove');
    } finally {
      setDismissingId(null);
    }
  };

  // Read/unread toggle (green dot). Optimistic update; reverts via refetch on error.
  const handleSetRead = async (candidateId, read) => {
    setSettingReadId(candidateId);
    setData((prev) =>
      prev
        ? {
            ...prev,
            recovered: (prev.recovered || []).map((r) =>
              r.candidateId === candidateId ? { ...r, isRead: read } : r
            ),
            failed: (prev.failed || []).map((r) =>
              r.candidateId === candidateId ? { ...r, isRead: read } : r
            ),
          }
        : prev
    );
    try {
      await axios.post(
        `/api/internal/extraction-fallbacks/${candidateId}/mark-${read ? 'read' : 'unread'}`
      );
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to update read state');
      fetchData(); // revert to server truth
    } finally {
      setSettingReadId(null);
    }
  };

  // Open the recruiter-style resume-score modal with the full candidate document.
  const openScore = async (candidateId) => {
    setScoreLoadingId(candidateId);
    try {
      const { data: res } = await axios.get(`/api/internal/candidate/${candidateId}`);
      if (res?.candidate) {
        setSelectedCandidate(res.candidate);
      } else {
        toast.error('Candidate details not available');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to load score');
    } finally {
      setScoreLoadingId(null);
    }
  };

  const summary = data?.summary || {};
  const breakdown = data?.breakdown || { byFileType: [], byOrg: [] };
  const rows = activeTab === 'failed' ? data?.failed || [] : data?.recovered || [];

  return (
    <>
      <AnimatedModal
        isOpen={isOpen}
        onClose={onClose}
        ariaLabelledBy="extraction-fallbacks-title"
        contentClassName="max-w-6xl rounded-2xl bg-white"
      >
        <div className="flex max-h-[85vh] flex-col">
          {/* Header */}
          <div className="flex items-start justify-between border-b border-gray-100 px-6 py-4">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-orange-500">
                <AlertTriangle className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 id="extraction-fallbacks-title" className="text-lg font-semibold text-gray-900">
                  Extraction Fallbacks
                </h2>
                <p className="mt-0.5 text-sm text-gray-500">
                  {summary.total ?? 0} of {summary.totalUploads ?? 0} uploads needed fallback ·{' '}
                  <span className="text-rose-600 font-medium">{summary.failed ?? 0} failed</span> ·{' '}
                  <span className="text-emerald-600 font-medium">{summary.recovered ?? 0} recovered</span> ·
                  est. cost {fmtUSD(summary.totalCostUsd)}
                </p>
              </div>
            </div>
            <button onClick={onClose} className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Controls: tabs + date range */}
          <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-3">
            <div className="flex gap-2">
              {[
                { key: 'failed', label: `Failed (${summary.failed ?? 0})` },
                { key: 'recovered', label: `Recovered (${summary.recovered ?? 0})` },
              ].map((t) => (
                <button
                  key={t.key}
                  onClick={() => setActiveTab(t.key)}
                  className={cn(
                    'rounded-lg px-4 py-1.5 text-sm font-medium transition-colors',
                    activeTab === t.key
                      ? 'bg-[#007BFF] text-white shadow-sm'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
                  )}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <div className="inline-flex items-center gap-2 text-xs">
              <span className="text-gray-500">Range:</span>
              <div className="inline-flex rounded-full bg-gray-100 p-0.5">
                {RANGE_OPTIONS.map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => setRange({ preset: opt.key })}
                    className={cn(
                      'rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors',
                      range.preset === opt.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900',
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Breakdown chips */}
          {(breakdown.byFileType.length > 0 || breakdown.byOrg.length > 0) && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-6 pb-2 text-xs text-gray-500">
              {breakdown.byFileType.length > 0 && (
                <span>
                  <span className="font-medium text-gray-600">By file type:</span>{' '}
                  {breakdown.byFileType.map((b) => `${b.type} (${b.count})`).join(', ')}
                </span>
              )}
              {breakdown.byOrg.length > 0 && (
                <span>
                  <span className="font-medium text-gray-600">By org:</span>{' '}
                  {breakdown.byOrg.slice(0, 5).map((b) => `${b.orgName} (${b.count})`).join(', ')}
                </span>
              )}
            </div>
          )}

          {/* Body */}
          <div className="min-h-[200px] flex-1 overflow-y-auto px-6 py-3">
            {loading ? (
              <div className="flex items-center justify-center py-16 text-gray-400">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : error ? (
              <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-600">{error}</div>
            ) : rows.length === 0 ? (
              <div className="py-16 text-center text-sm text-gray-400">
                No {activeTab} extractions in this range.
              </div>
            ) : (
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-xs uppercase tracking-wide text-gray-400">
                    <th className="py-2 pr-3 font-medium">Candidate</th>
                    <th className="py-2 pr-3 font-medium">Organisation</th>
                    <th className="py-2 pr-3 font-medium">File</th>
                    <th className="py-2 pr-3 font-medium">Reason (LibreOffice failure)</th>
                    {activeTab === 'recovered' && <th className="py-2 pr-3 font-medium">Score</th>}
                    <th className="py-2 pr-3 font-medium">Date</th>
                    <th className="py-2 pr-3 font-medium">Status</th>
                    <th className="py-2 pr-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.candidateId} className="border-b border-gray-50 hover:bg-gray-50/50">
                      <td className="py-2.5 pr-3">
                        <div className="flex items-center gap-1.5 font-medium text-gray-900">
                          <span>{r.name || '—'}</span>
                          {!r.isRead && (
                            <span
                              className="inline-block h-2 w-2 shrink-0 rounded-full bg-emerald-500 ring-2 ring-white"
                              title="Unread"
                              aria-label="Unread"
                            />
                          )}
                        </div>
                        <div className="text-xs text-gray-400">{r.email || ''}</div>
                      </td>
                      <td className="py-2.5 pr-3 text-gray-600">{r.orgName}</td>
                      <td className="py-2.5 pr-3 uppercase text-gray-500">{r.originalExtension || '—'}</td>
                      <td className="py-2.5 pr-3 max-w-[240px] truncate text-gray-500" title={r.conversionError}>
                        {r.conversionError || '—'}
                      </td>
                      {activeTab === 'recovered' && (
                        <td className="py-2.5 pr-3">
                          {r.score != null ? (
                            <button
                              onClick={() => openScore(r.candidateId)}
                              disabled={scoreLoadingId === r.candidateId}
                              title="View score breakdown"
                              className="inline-flex items-center gap-1 rounded-md bg-indigo-50 px-2 py-1 text-xs font-semibold text-indigo-700 hover:bg-indigo-100 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-60"
                            >
                              {scoreLoadingId === r.candidateId ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <>
                                  {r.score}
                                  <Eye className="h-3.5 w-3.5" />
                                </>
                              )}
                            </button>
                          ) : (
                            '—'
                          )}
                        </td>
                      )}
                      <td className="py-2.5 pr-3 text-gray-500">{fmtDate(r.createdAt)}</td>
                      <td className="py-2.5 pr-3">
                        <select
                          value={r.isRead ? 'read' : 'unread'}
                          disabled={settingReadId === r.candidateId}
                          onChange={(e) => handleSetRead(r.candidateId, e.target.value === 'read')}
                          aria-label={r.isRead ? 'Mark as unread' : 'Mark as read'}
                          className="cursor-pointer rounded-md border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-700 shadow-sm focus:border-[#007BFF] focus:outline-none focus:ring-1 focus:ring-[#007BFF]/40 disabled:opacity-60"
                        >
                          <option value="unread">{r.isRead ? 'Mark as unread' : 'Unread'}</option>
                          <option value="read">{r.isRead ? 'Read' : 'Mark as read'}</option>
                        </select>
                      </td>
                      <td className="py-2.5 pr-3">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openResume(r.candidateId)}
                            className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-600 hover:bg-gray-100"
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </button>
                          {activeTab === 'failed' && (
                            <button
                              onClick={() => handleRetry(r.candidateId)}
                              disabled={retryingId === r.candidateId}
                              className="inline-flex items-center gap-1 rounded-md bg-[#007BFF] px-2 py-1 text-xs font-medium text-white hover:bg-[#0069d9] disabled:opacity-60"
                            >
                              {retryingId === r.candidateId ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <RotateCcw className="h-3.5 w-3.5" />
                              )}
                              Retry
                            </button>
                          )}
                          <button
                            onClick={() => handleDismiss(r.candidateId)}
                            disabled={dismissingId === r.candidateId}
                            title="Remove from this panel (does not delete the candidate)"
                            className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-500 hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200 disabled:opacity-60"
                          >
                            {dismissingId === r.candidateId ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Trash2 className="h-3.5 w-3.5" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </AnimatedModal>

      {/* Resume-score modal — same component the recruiter uses, opened on the score tab. */}
      {selectedCandidate && (
        <CandidateInsightModal
          isOpen={!!selectedCandidate}
          candidate={selectedCandidate}
          initialTab="score"
          onClose={() => setSelectedCandidate(null)}
        />
      )}
    </>
  );
}
